import { tableControlSurfaceBackground } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  tableV2NestedBodyRowHeightPx,
  tableV2NestedHeaderHeight,
} from '@/features/shared/components/tableSystem/TableV2Constants';
import { isLightTheme } from '@/theme/semanticTokens';
import { s, typographyTokens } from '@/theme/tokens';
import { MAX_PAGE_SIZE, type QueryResult } from '@flowqa/shared';
import type { SxProps, Theme } from '@/ui/primitives';

type ThemeSxObject = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;

export type TableV2NestedTableDepth = 1 | 2 | 3 | 4;

export const tableV2NestedPrimaryBackground = tableControlSurfaceBackground;
export const tableV2NestedSecondaryBackground = 'var(--surface-card)';
export const tableV2NestedAlternateDepthBackground = 'var(--surface-page)';
export const tableV2NestedBackgroundByDepth = {
  1: tableV2NestedPrimaryBackground,
  2: tableV2NestedAlternateDepthBackground,
  3: tableV2NestedPrimaryBackground,
  4: tableV2NestedAlternateDepthBackground,
} satisfies Record<TableV2NestedTableDepth, string>;

export const resolveTableV2NestedBackground = (depth: TableV2NestedTableDepth): string =>
  tableV2NestedBackgroundByDepth[depth];

const resolveTableV2NestedAltBackground = (theme: Theme): string =>
  isLightTheme(theme) ? tableV2NestedSecondaryBackground : tableV2NestedAlternateDepthBackground;

export const resolveTableV2NestedDividerColor = (theme: Theme): string => theme.app.colors.border;

type TableV2NestedQueryInput = {
  page: number;
  pageSize: number;
};

export const queryAllTableV2NestedRows = async <TRow, TInput extends TableV2NestedQueryInput>(
  input: TInput,
  queryPage: (input: TInput) => Promise<QueryResult<TRow>>,
): Promise<QueryResult<TRow>> => {
  const firstPageInput = { ...input, page: 1, pageSize: MAX_PAGE_SIZE };
  const firstPage = await queryPage(firstPageInput);
  const pageCount = Math.max(1, Math.ceil(firstPage.total / MAX_PAGE_SIZE));

  if (pageCount === 1) return firstPage;

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, async (_item, index) =>
      queryPage({ ...input, page: index + 2, pageSize: MAX_PAGE_SIZE }),
    ),
  );

  return {
    ...firstPage,
    items: [...firstPage.items, ...remainingPages.flatMap((remainingPage) => remainingPage.items)],
    page: 1,
    pageSize: MAX_PAGE_SIZE,
  };
};

export const tableV2NestedTableFrameSx = {
  overflow: 'hidden',
} satisfies SxProps<Theme>;

export const tableV2NestedAltTableFrameSx = {
  overflow: 'hidden',
} satisfies SxProps<Theme>;

export const tableV2NestedTableSx = {
  backgroundColor: tableV2NestedPrimaryBackground,
  border: 0,
  borderCollapse: 'separate',
  borderSpacing: 0,
  tableLayout: 'fixed',
  '&& > thead > tr > th': {
    backgroundColor: tableV2NestedPrimaryBackground,
  },
  '& > thead > tr > th': {
    backgroundColor: tableV2NestedPrimaryBackground,
    borderBottom: (theme) => `1px solid ${resolveTableV2NestedDividerColor(theme)}`,
    color: (theme) => theme.app.colors.textMuted,
    fontSize: '13px',
    fontWeight: typographyTokens.bodyMedium.fontWeight,
    height: tableV2NestedHeaderHeight,
    px: s('lg'),
    py: s('none'),
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  '& > tbody > tr > td': {
    backgroundColor: tableV2NestedPrimaryBackground,
    boxSizing: 'border-box',
    fontSize: typographyTokens.body.fontSize,
    height: `${String(tableV2NestedBodyRowHeightPx)}px`,
    maxHeight: `${String(tableV2NestedBodyRowHeightPx)}px`,
    px: s('lg'),
    py: '0 !important',
    verticalAlign: 'middle',
  },
  '& .MuiTypography-root': {
    fontSize: typographyTokens.body.fontSize,
  },
  '& > tbody > tr:last-of-type > td': {
    borderBottom: 0,
  },
} satisfies SxProps<Theme>;

export const tableV2NestedAltTableSx = {
  backgroundColor: resolveTableV2NestedAltBackground,
  border: 0,
  borderCollapse: 'separate',
  borderSpacing: 0,
  tableLayout: 'fixed',
  '&& > thead > tr > th': {
    backgroundColor: resolveTableV2NestedAltBackground,
  },
  '& > thead > tr > th': {
    backgroundColor: resolveTableV2NestedAltBackground,
    borderBottom: (theme) => `1px solid ${resolveTableV2NestedDividerColor(theme)}`,
    color: (theme) => theme.app.colors.textMuted,
    fontSize: '13px',
    fontWeight: typographyTokens.bodyMedium.fontWeight,
    height: tableV2NestedHeaderHeight,
    px: s('lg'),
    py: s('none'),
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  '& > tbody > tr > td': {
    backgroundColor: resolveTableV2NestedAltBackground,
    boxSizing: 'border-box',
    fontSize: typographyTokens.body.fontSize,
    height: `${String(tableV2NestedBodyRowHeightPx)}px`,
    maxHeight: `${String(tableV2NestedBodyRowHeightPx)}px`,
    px: s('lg'),
    py: '0 !important',
    verticalAlign: 'middle',
  },
  '& .MuiTypography-root': {
    fontSize: typographyTokens.body.fontSize,
  },
  '& > tbody > tr:last-of-type > td': {
    borderBottom: 0,
  },
} satisfies SxProps<Theme>;

export const tableV2NestedContentSx = {
  display: 'grid',
  pb: s('lg'),
  pt: s('xs'),
  px: s('lg'),
} satisfies SxProps<Theme>;

export const tableV2NestedCarrierRowSx = {
  backgroundColor: tableControlSurfaceBackground,
  height: '0 !important',
  maxHeight: 'none !important',
  '& > td': {
    borderBottom: 0,
    height: '0 !important',
    lineHeight: 0,
    maxHeight: 'none !important',
    overflow: 'hidden',
    p: s('none'),
    verticalAlign: 'top',
  },
} satisfies SxProps<Theme>;

export const tableV2NestedBodyRowSx = {
  backgroundColor: tableV2NestedPrimaryBackground,
  '& > td': {
    backgroundColor: tableV2NestedPrimaryBackground,
  },
} satisfies SxProps<Theme>;

export const tableV2NestedBodyAltRowSx = {
  backgroundColor: resolveTableV2NestedAltBackground,
  '& > td': {
    backgroundColor: resolveTableV2NestedAltBackground,
  },
} satisfies SxProps<Theme>;

export const tableV2NestedDepthSx = {
  1: {
    bodyRow: tableV2NestedBodyRowSx,
    frame: tableV2NestedTableFrameSx,
    table: tableV2NestedTableSx,
  },
  2: {
    bodyRow: tableV2NestedBodyAltRowSx,
    frame: tableV2NestedAltTableFrameSx,
    table: tableV2NestedAltTableSx,
  },
  3: {
    bodyRow: tableV2NestedBodyRowSx,
    frame: tableV2NestedTableFrameSx,
    table: tableV2NestedTableSx,
  },
  4: {
    bodyRow: tableV2NestedBodyAltRowSx,
    frame: tableV2NestedAltTableFrameSx,
    table: tableV2NestedAltTableSx,
  },
} satisfies Record<
  TableV2NestedTableDepth,
  {
    bodyRow: SxProps<Theme>;
    frame: SxProps<Theme>;
    table: SxProps<Theme>;
  }
>;

type BuildTableV2NestedBodyRowSxArgs = {
  depth: TableV2NestedTableDepth;
  hasRenderedDivider: boolean;
};

export const buildTableV2NestedBodyRowSx = ({
  depth,
  hasRenderedDivider,
}: BuildTableV2NestedBodyRowSxArgs): ThemeSxObject => ({
  ...(tableV2NestedDepthSx[depth].bodyRow as Record<string, unknown>),
  '& > td': {
    borderBottom: hasRenderedDivider ? (theme: Theme) => `1px solid ${theme.app.colors.border}` : 0,
    height: `${String(tableV2NestedBodyRowHeightPx)}px`,
    py: s('sm'),
    verticalAlign: 'middle',
  },
});

export const tableV2NestedDetailCellSx = {
  backgroundColor: tableControlSurfaceBackground,
  borderBottom: 0,
  lineHeight: 1,
  px: `${s('none')} !important`,
  py: `${s('sm')} !important`,
} satisfies SxProps<Theme>;
